import { z } from "zod";
/**
 * Shared request/response schemas for the profile service and the glowin Console.
 * Both sides validate with the SAME schema so the client can't send a shape the
 * server rejects (and vice versa).
 */
/** Money as minor units + ISO currency — never a bare float. */
export const moneySchema = z.object({
    amountMinor: z.number().int(),
    currency: z.string().length(3)
});
/** A name that can be shown in either language. `en` is required as the fallback. */
export const localizedNameSchema = z.object({
    en: z.string().min(1).max(120),
    zh: z.string().max(120).optional()
});
// --- Hours -------------------------------------------------------------------
/** "HH:MM" 24-hour local wall-clock time. */
export const timeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected "HH:MM" 24-hour time');
/**
 * One open interval. `close` may be earlier than `open` to express an overnight
 * span (e.g. 17:00 → 01:00).
 */
export const openIntervalSchema = z.object({
    open: timeOfDaySchema,
    close: timeOfDaySchema
});
export const weekdaySchema = z.enum(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);
/** Multiple intervals per day supports the common lunch/dinner split. */
export const weeklyHoursSchema = z.record(weekdaySchema, z.array(openIntervalSchema));
/** A specific local date that overrides the weekly schedule (holiday, private event). */
export const hoursOverrideSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD local date"),
    closed: z.boolean().default(false),
    intervals: z.array(openIntervalSchema).default([]),
    note: z.string().max(200).optional()
});
/** Ad-hoc "closed right now" — beats editing the schedule during a rush. */
export const temporaryClosureSchema = z.object({
    untilIso: z.string().datetime(),
    reason: z.string().max(200).optional()
});
export const hoursSchema = z.object({
    weekly: weeklyHoursSchema,
    overrides: z.array(hoursOverrideSchema).default([]),
    temporaryClosure: temporaryClosureSchema.optional()
});
// --- Profile & settings ------------------------------------------------------
/**
 * Languages the agent can open a call in. Extend freely — the model speaks far more than
 * this; the list exists so the console can offer a picker instead of a text box.
 */
export const GREETING_LANGUAGES = ["en", "zh", "es", "ko", "ja", "vi", "th", "fr"];
/** Display labels, in the language itself so an owner recognises their own. */
export const GREETING_LANGUAGE_LABELS = {
    en: "English",
    zh: "中文 (Chinese)",
    es: "Español (Spanish)",
    ko: "한국어 (Korean)",
    ja: "日本語 (Japanese)",
    vi: "Tiếng Việt (Vietnamese)",
    th: "ไทย (Thai)",
    fr: "Français (French)"
};
export const restaurantProfileSchema = z.object({
    /**
     * Spoken name, one language. Unlike MENU items — which stay bilingual, because the agent
     * reads dish names aloud to callers in either language — a restaurant answers the phone
     * with a single name.
     */
    displayName: z.string().min(1).max(120),
    /** E.164, e.g. "+15074362478". Maps a dialed number to this restaurant. */
    phoneE164: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, "expected E.164 phone, e.g. +15074362478"),
    /**
     * Where the agent sends a caller who needs a human, e.g. the front desk.
     *
     * Distinct from phoneE164: that is the number CUSTOMERS DIAL and only glowin support may
     * change it (claiming another restaurant's number would divert its calls). This one is
     * the restaurant's own staff line and owners may set it freely.
     *
     * Optional: with no number set, the agent must tell the caller it cannot transfer rather
     * than claim it did.
     */
    staffPhoneE164: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, "expected E.164 phone, e.g. +15551234567")
        .optional(),
    address: z.string().max(300).optional(),
    /** IANA zone, e.g. "America/Los_Angeles". Hours are local wall-clock in this zone. */
    timezone: z.string().min(1),
    /**
     * Language the agent GREETS in. After the greeting it follows the caller, switching if
     * they switch — that behaviour comes from the prompt and needs no configuration, so this
     * is only the opening move, not a restriction on what the agent may speak.
     *
     * Enumerated rather than free BCP-47 text: a typo would make the agent open a real call in
     * the wrong language, or ignore the setting silently.
     */
    preferredLanguage: z.enum(GREETING_LANGUAGES).default("en")
});
/** Built-in Realtime voices. Order puts the recommended ones first for the console. */
export const REALTIME_VOICES = [
    "marin",
    "cedar",
    "alloy",
    "ash",
    "ballad",
    "coral",
    "echo",
    "sage",
    "shimmer",
    "verse"
];
/**
 * Owner-supplied opening line. Optional — a sensible default is generated from the
 * restaurant name when unset.
 *
 * This text is placed into the agent's system prompt, so it is constrained deliberately:
 *   - single line (newlines stripped by the console, rejected here) so it can't append
 *     instructions of its own beneath the greeting;
 *   - short, because a long greeting wastes call time and tokens on every single call.
 * The blast radius is bounded to the owner's own restaurant either way — they can only
 * affect how their own agent behaves — but a greeting is a spoken sentence, not a place to
 * put directions.
 */
const greetingSchema = z
    .string()
    .min(1)
    .max(200)
    .refine((g) => !/[\r\n]/.test(g), { message: "the greeting must be a single line" })
    .refine((g) => g.trim().length > 0, { message: "the greeting cannot be blank" });
/**
 * The greeting used when a restaurant hasn't written its own, IN ITS PREFERRED LANGUAGE.
 *
 * This used to always be English, which quietly defeated the preferred-language setting: the
 * prompt said "greet in Chinese. Say exactly: <English sentence>", and "say exactly" wins. The
 * language has to be in the TEXT, not in an instruction wrapped around it.
 *
 * Shared so the console shows exactly what callers will hear rather than an approximation.
 */
export function defaultGreeting(displayName, language = "en") {
    const template = DEFAULT_GREETINGS[language] ?? DEFAULT_GREETINGS.en;
    return template.replace("{name}", displayName);
}
/**
 * One per offered language. Deliberately literal translations of the same short sentence —
 * a greeting should sound like a person answering a phone, not like a translation.
 *
 * AN OFFER OF HELP, NOT AN ORDER PROMPT. Every one of these used to ask what the caller wanted to order,
 * which presumes why they rang. They also ask whether the restaurant is open, where it is, what is on the
 * menu, and about an order already placed — and opening with "what can I get you?" makes each of those
 * callers correct the question before they can ask theirs.
 */
const DEFAULT_GREETINGS = {
    en: "Thanks for calling {name}! How can I help you?",
    zh: "感谢致电{name}！请问有什么可以帮您？",
    es: "¡Gracias por llamar a {name}! ¿En qué puedo ayudarle?",
    ko: "{name}에 전화 주셔서 감사합니다! 무엇을 도와드릴까요?",
    ja: "{name}にお電話ありがとうございます！どのようなご用件でしょうか？",
    vi: "Cảm ơn bạn đã gọi {name}! Tôi có thể giúp gì cho bạn?",
    th: "ขอบคุณที่โทรมาที่ {name} ค่ะ! มีอะไรให้ช่วยไหมคะ?",
    fr: "Merci d'appeler {name} ! Comment puis-je vous aider ?"
};
// --- Reservations ------------------------------------------------------------
/** A local calendar date at the restaurant, YYYY-MM-DD. */
export const localDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD local date");
/**
 * The restaurant's rules for tables booked over the phone.
 *
 * OFF unless the restaurant turns it on. While off, the agent is not given the booking tools and its prompt
 * never mentions tables, so a caller who asks is offered a message instead of a booking nobody expects.
 *
 * Every limit bounds what an UNATTENDED agent may promise. Staff adding a booking in the console are not held
 * to them: the restaurant knows its own floor, and a phone line answered at 10pm does not.
 */
export const reservationSettingsSchema = z.object({
    enabled: z.boolean().default(false),
    /** Bigger groups are offered a message or a transfer instead of a booking. */
    maxPartySize: z.number().int().min(1).max(50).default(6),
    /** Guests who may arrive in one 30-minute slot, counted across every booking in it. */
    maxGuestsPerSlot: z.number().int().min(1).max(500).default(20),
    /** How far ahead a caller may book, in days. */
    maxDaysAhead: z.number().int().min(1).max(180).default(30),
    /** How soon a booking may start, in minutes from now. */
    minNoticeMinutes: z.number().int().min(0).max(1440).default(60),
    /** The latest arrival, in minutes before closing. */
    lastSeatingMinutesBeforeClose: z.number().int().min(0).max(240).default(60)
});
/** Arrival slots are this long. Fixed: one rule for the restaurant to reason about. */
export const RESERVATION_SLOT_MINUTES = 30;
/**
 * What the reservation service needs from profile to judge a booking: the timezone, the rules, and each
 * requested day's EFFECTIVE opening intervals.
 *
 * Profile resolves date overrides against the weekly schedule, because profile owns what hours mean; the
 * reservation service only does slot arithmetic on the result. An interval whose close is not after its open
 * runs past midnight, exactly as in openIntervalSchema.
 *
 * Declared here, in the one package both sides import, so the producer and the consumer cannot drift apart.
 */
export const bookingRulesSchema = z.object({
    timezone: z.string().min(1),
    reservations: reservationSettingsSchema,
    days: z.array(z.object({
        date: localDateSchema,
        intervals: z.array(openIntervalSchema),
        /** Closed all day by a date override. */
        closed: z.boolean().default(false),
        note: z.string().optional()
    })),
    /** An active "paused" period. A booking that would start before it ends is refused. */
    closedUntilIso: z.string().datetime().optional()
});
/**
 * CONFIRMED is the only state a caller can cancel from. SEATED and NO_SHOW are what staff record afterwards,
 * and either can be undone back to CONFIRMED. CANCELLED is final, and frees the seats.
 */
export const RESERVATION_STATUSES = ["CONFIRMED", "SEATED", "NO_SHOW", "CANCELLED"];
export const reservationStatusSchema = z.enum(RESERVATION_STATUSES);
/** A booking as staff see it. */
export const reservationViewSchema = z.object({
    reservationId: z.string(),
    date: localDateSchema,
    time: timeOfDaySchema,
    startsAtIso: z.string(),
    partySize: z.number().int(),
    name: z.string(),
    phoneE164: z.string().optional(),
    notes: z.string().optional(),
    status: reservationStatusSchema,
    /** PHONE: the agent booked it. STAFF: somebody added it in the console. */
    source: z.enum(["PHONE", "STAFF"]),
    createdAtIso: z.string()
});
/** A booking staff add by hand. Not held to the phone limits; see reservationSettingsSchema. */
export const staffReservationRequestSchema = z.object({
    date: localDateSchema,
    time: timeOfDaySchema,
    partySize: z.number().int().min(1).max(500),
    name: z.string().trim().min(1, "Enter a name.").max(80),
    phoneE164: z
        .string()
        .regex(/^\+[1-9]\d{7,14}$/, "Enter the number with its country code, e.g. +19495551234.")
        .optional(),
    notes: z.string().trim().max(300).optional()
});
export const reservationStatusChangeSchema = z.object({ status: reservationStatusSchema });
export const restaurantSettingsSchema = z.object({
    /** What the agent says first. Falls back to a default built from the restaurant name. */
    greeting: greetingSchema.optional(),
    /** Pickup ETA quoted to the caller. Replaces the hardcoded 15 minutes. */
    prepTimeMinutes: z.number().int().min(1).max(240).default(15),
    maxItemsPerOrder: z.number().int().min(1).max(100).default(50),
    /**
     * OpenAI Realtime voice. Enumerated, not free text: an unrecognised name fails the
     * session at call time, which would mean a silent phone line rather than a validation
     * error. marin and cedar are the newest and recommended for quality.
     *
     * The voice is fixed for a session once the model has emitted audio, so it is applied at
     * session creation — a change here takes effect on the NEXT call, not a call in progress.
     */
    voice: z.enum(REALTIME_VOICES).default("marin"),
    speechSpeed: z.number().min(0.25).max(1.5).default(1.1),
    /**
     * Record this restaurant's calls, so staff can play them back on the Calls page.
     *
     * OFF unless the restaurant turns it on. Recording a restaurant's customers is the restaurant's decision,
     * not our default; every caller already hears "this call may be recorded", but that notice permits
     * recording, it does not require it. Recordings are kept for 7 days.
     */
    recordCalls: z.boolean().default(false),
    /** Table bookings over the phone. Off by default; see reservationSettingsSchema. */
    reservations: reservationSettingsSchema.default({})
});
// --- Menu --------------------------------------------------------------------
export const modifierSchema = z.object({
    id: z.string().min(1).max(60),
    name: localizedNameSchema,
    priceDelta: moneySchema
});
/**
 * Shape of a reusable option group. Declared once so the create-form (no id yet) and the
 * update-form can't drift apart.
 */
const modifierGroupShape = {
    id: z.string().min(1).max(60),
    name: localizedNameSchema,
    required: z.boolean(),
    minSelect: z.number().int().min(0),
    maxSelect: z.number().int().min(1),
    options: z.array(modifierSchema).min(1)
};
/**
 * Rules that span fields, so they can't live on individual fields. Returned as a message
 * rather than thrown, so both group schemas apply the identical checks via superRefine —
 * a shared generic wrapper would erase the inferred types.
 */
function groupCoherenceProblem(g) {
    if (g.maxSelect < g.minSelect)
        return "maxSelect must be >= minSelect";
    if (g.maxSelect > g.options.length)
        return "maxSelect cannot exceed the number of options";
    // "Required" but satisfiable by choosing nothing is a contradiction the agent acts on:
    // it enforces required choices through minSelect.
    if (g.required && g.minSelect < 1) {
        return "a required group must ask for at least one choice (minSelect >= 1)";
    }
    if (new Set(g.options.map((o) => o.id)).size !== g.options.length) {
        return "option ids must be unique within a group";
    }
    return undefined;
}
const checkGroup = (g, ctx) => {
    const problem = groupCoherenceProblem(g);
    if (problem)
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
};
export const modifierGroupSchema = z.object(modifierGroupShape).superRefine(checkGroup);
/** Creating a group: the server mints the id. */
export const newModifierGroupSchema = z
    .object(modifierGroupShape)
    .omit({ id: true })
    .superRefine(checkGroup);
export const menuCategorySchema = z.object({
    id: z.string().min(1).max(60),
    name: localizedNameSchema,
    sortIndex: z.number().int().min(0)
});
export const menuItemSchema = z.object({
    itemId: z.string().min(1).max(60),
    name: localizedNameSchema,
    description: z.string().max(500).optional(),
    basePrice: moneySchema,
    categoryId: z.string().min(1).max(60),
    sortIndex: z.number().int().min(0).default(0),
    /** false = sold out. Sold-out items are withheld from the agent entirely. */
    available: z.boolean().default(true),
    /** References into the reusable group library. */
    groupIds: z.array(z.string().min(1)).default([])
});
/**
 * Creating an item: everything except the id and sortIndex, which the SERVER assigns.
 * A client-chosen id could collide or bake in a typo permanently.
 */
export const newMenuItemSchema = menuItemSchema.omit({ itemId: true, sortIndex: true }).extend({
    groupIds: z.array(z.string().min(1)).default([]),
    available: z.boolean().default(true)
});
export const menuDraftSchema = z.object({
    categories: z.array(menuCategorySchema),
    items: z.array(menuItemSchema),
    modifierGroups: z.array(modifierGroupSchema)
});
// --- Request bodies ----------------------------------------------------------
export const availabilityPatchSchema = z.object({ available: z.boolean() });
export const publishRequestSchema = z.object({
    /** Optional note stored with the version, e.g. "raised prices 5%". */
    note: z.string().max(200).optional()
});
export const rollbackRequestSchema = z.object({
    version: z.number().int().min(1)
});
/**
 * Whether this restaurant's number will actually reach it.
 *
 * Kept next to the mapping rather than in the portal, so that a future admin API — or a scheduled audit —
 * answers the question identically instead of re-deriving it from the same two fields.
 */
export function phoneRoutingFault(entry) {
    const { phoneE164, indexedPhoneE164 } = entry;
    if (!phoneE164 && !indexedPhoneE164)
        return "no-number";
    // In the index but not in the profile is still routable, so it is a mismatch rather than a missing key:
    // calls arrive, and the profile disagrees about which number it answers.
    if (!indexedPhoneE164)
        return "missing-index-key";
    if (!phoneE164)
        return "index-key-mismatch";
    return phoneE164 === indexedPhoneE164 ? undefined : "index-key-mismatch";
}
/**
 * Turn what support was told into the E.164 string the index is keyed on.
 *
 * Nobody reads a number back in E.164. It arrives as "(626) 508-1917", or "626-508-1917", or with the
 * country code missing entirely, because that is how people say their own number. The index key is exact,
 * so an un-normalised query matches nothing — and "no calls found" is indistinguishable from "this person
 * never called", which sends support to the wrong conclusion about whose fault the problem is.
 *
 * NANP ONLY for the 10-digit case. Assuming +1 for ten digits is right for every number we serve today and
 * wrong the moment that changes, so it is done explicitly here rather than buried in a regex, and an
 * 11-digit string starting 1 is treated as already carrying its country code.
 *
 * Returns undefined rather than a guess when the digits cannot be a phone number. A near-miss query that
 * silently searched for something else would be worse than being told to check the number.
 */
export function normalizeCallerNumber(raw) {
    const trimmed = raw.trim();
    if (trimmed.length === 0)
        return undefined;
    // A leading + is the only thing that marks an already-international number, so it is remembered before
    // the punctuation goes.
    const explicitlyInternational = trimmed.startsWith("+");
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length === 0)
        return undefined;
    if (explicitlyInternational) {
        // E.164 allows up to 15 digits and needs at least 8 to be a real number anywhere.
        return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : undefined;
    }
    if (digits.length === 10)
        return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1"))
        return `+${digits}`;
    return undefined;
}
/** Whether a string looks like a Twilio call sid, so a search box can route it without asking. */
export function isCallSid(raw) {
    // CA + 32 hex characters. Checked strictly: a loose match would send a restaurant id or an order id down
    // the call-lookup path and report "not found" for something that exists under a different name.
    return /^CA[0-9a-f]{32}$/i.test(raw.trim());
}
/**
 * One diagnosis per account, most blocking first.
 *
 * Order is the point. An account can be both unconfirmed and membershipless, and telling support about the
 * grant first would have them arrange access for someone who cannot reach the door yet.
 */
export function diagnoseAccount(state) {
    if (!state.exists)
        return "no-account";
    if (state.enabled === false)
        return "disabled";
    if (state.status === "UNCONFIRMED")
        return "never-confirmed";
    if (state.status === "FORCE_CHANGE_PASSWORD")
        return "password-never-set";
    if (state.status === "RESET_REQUIRED")
        return "password-reset-required";
    /*
     * Checked after the status cases because an unconfirmed account is ALSO unverified, and "they never
     * entered the code" is the actionable half of that. Reached here, the account is confirmed and usable
     * while its address is not proven — which is exactly the state that grants no operator rights and which
     * only ever arises from an account created administratively.
     */
    if (state.emailVerified === false)
        return "email-unverified";
    if (state.restaurants.length === 0 && !state.isOperator)
        return "signs-in-but-sees-nothing";
    return "healthy";
}
